import type { EngagementSeries, ListingTransaction, RiskLevel } from '@/client/types/promotion';

// ── Formatting ────────────────────────────────────────────────────────────────

const ghs0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'GHS', maximumFractionDigits: 0 });
const ghsCompact = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'GHS',
  notation: 'compact',
  maximumFractionDigits: 1,
});
export const money = (n: number) => ghs0.format(n);
export const moneyCompact = (n: number) => ghsCompact.format(n);

export const fmtDate = (iso: string | null) =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

// ── Risk model ────────────────────────────────────────────────────────────────
//
// `risk` and `fill` are computed server-side (high < 0.4 ≤ medium < 0.75 ≤ low);
// these are the display labels for whichever band the API returns.

export const RISK_META: Record<RiskLevel, { label: string; badge: string; dot: string; blurb: string }> = {
  high: {
    label: 'High risk',
    badge: 'bg-rose-50 text-rose-700 ring-rose-600/20',
    dot: 'bg-rose-500',
    blurb: 'Impressions far below target — under-delivering',
  },
  medium: {
    label: 'Medium risk',
    badge: 'bg-amber-50 text-amber-700 ring-amber-600/20',
    dot: 'bg-amber-500',
    blurb: 'Impressions trailing target',
  },
  low: {
    label: 'Low risk',
    badge: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
    dot: 'bg-emerald-500',
    blurb: 'Impressions on or near target',
  },
};

// ── Spend over time ───────────────────────────────────────────────────────────

export interface SpendRow {
  label: string;
  key: string; // YYYY-MM for stable sort
  amount: number;
  refunded: number;
}

/** Bin transactions into monthly spend buckets, oldest → newest. */
export function spendByMonth(transactions: ListingTransaction[]): SpendRow[] {
  const map = new Map<string, SpendRow>();
  for (const t of transactions) {
    if (t.status === 'pending' || !t.date) continue;
    const d = new Date(`${t.date}T00:00:00`);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const row =
      map.get(key) ??
      ({
        key,
        label: d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' }),
        amount: 0,
        refunded: 0,
      } as SpendRow);
    if (t.status === 'refunded') row.refunded += t.amount;
    else row.amount += t.amount;
    map.set(key, row);
  }
  return [...map.values()].sort((a, b) => a.key.localeCompare(b.key));
}

// ── Engagement shaping ────────────────────────────────────────────────────────

export const ENGAGEMENT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export type EngagementRow = { label: string; total: number } & Record<string, number | string>;

export interface EngagementChartSeries {
  engtid: number;
  name: string;
  name_system: string;
  /** Sum across the year — what the series is ranked and filtered on. */
  total: number;
}

export interface EngagementChart {
  rows: EngagementRow[];
  /** Engagement types that recorded something, busiest first. */
  series: EngagementChartSeries[];
}

/**
 * Same shape as the revenue trend: one flat row per bucket plus an explicit
 * series list. Types that recorded nothing all year are dropped so the toggles
 * and legend only offer series that can actually draw; the months a kept series
 * sat at zero stay in the row, so they still read out in the tooltip.
 */
export function toEngagementSeries(series: EngagementSeries[]): EngagementChart {
  const charted = series
    .map((s) => ({
      engtid: s.engtid,
      name: s.name,
      name_system: s.name_system,
      total: (s.monthly ?? []).reduce((sum, v) => sum + (v ?? 0), 0),
    }))
    .filter((s) => s.total > 0)
    .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));

  const byId = new Map(series.map((s) => [s.engtid, s]));
  const rows = ENGAGEMENT_MONTHS.map((label, i) => {
    const row = { label, total: 0 } as EngagementRow;
    charted.forEach((s) => {
      const value = byId.get(s.engtid)?.monthly?.[i] ?? 0;
      row[s.name] = value;
      row.total = (row.total as number) + value;
    });
    return row;
  });

  return { rows, series: charted };
}
